import type { ReactNode } from 'react';
import React from 'react';
import { Box, Divider, Icon, MediaObject, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconChecklistFilled } from '@zillow/constellation-icons';
import { chatBubbleStyles } from '../../ChatBubble/styles';
import { systemMessageBubbleStyles } from './styles';
import ChatAvatar from '../../ChatAvatar';
import MessageFooter from '../../MessageFooter';
import {
  LANDLORD_SYSTEM_MESSAGE_ICON_MAP,
  RENTER_SYSTEM_MESSAGE_ICON_MAP,
} from '../../../constants/systemMessageIcons';
import type { Persona } from '../../../types/persona';

export type HighPrioritySystemMessageProps = {
  messageDateMs: string;
  messageType: string;
  uiTreatmentType: string;
  ctaText?: string;
  message: string;
  onCtaClick?: () => void;
  renderLink?: (children: ReactNode) => ReactNode;
  shouldDisplayAsPreview?: boolean;
  title: string;
  persona?: Persona;
};

export default function HighPrioritySystemMessage({
  ctaText,
  message,
  messageDateMs,
  messageType,
  onCtaClick,
  renderLink,
  persona = 'landlord',
  shouldDisplayAsPreview = false,
  title,
  uiTreatmentType,
}: HighPrioritySystemMessageProps) {
  const hasTitle = !!title;
  const hasMessage = !!message;
  const hasCtaText = !!ctaText;

  // Determine if icon should be shown based on uiTreatmentType
  const shouldShowIcon = uiTreatmentType?.includes('highPriorityWithLogo');

  // Get icon from persona-specific mapping
  const iconMap = persona === 'renter' ? RENTER_SYSTEM_MESSAGE_ICON_MAP : LANDLORD_SYSTEM_MESSAGE_ICON_MAP;
  const IconComponent = iconMap[messageType as keyof typeof iconMap] ?? IconChecklistFilled;

  return (
    <MediaObject
      media={<ChatAvatar type="zillow" hasTimestampInMessage={!shouldDisplayAsPreview} />}
      css={{
        alignItems: 'end',
        gap: 0,
      }}
    >
      <Box css={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          data-testid="high-priority-system-message"
          css={{
            ...chatBubbleStyles,
            ...systemMessageBubbleStyles,
          }}
        >
          <Box css={{ display: 'flex', flexDirection: 'row', gap: 'default', padding: 'default' }}>
            {shouldShowIcon && (
              <Icon
                role="img"
                size="xl"
                css={{
                  color: 'icon.express.inspire.hero',
                  alignSelf: 'center',
                }}
              >
                {React.createElement(IconComponent)}
              </Icon>
            )}
            <Box>
              {hasTitle && <Paragraph textStyle="body-bold">{title}</Paragraph>}
              {hasMessage && <Paragraph>{message}</Paragraph>}
            </Box>
          </Box>

          {hasCtaText && <Divider css={{ backgroundColor: 'border.muted' }} />}
          {hasCtaText && !shouldDisplayAsPreview && (
            <Box
              css={{
                padding: 'default',
                textAlign: 'center',
              }}
            >
              {renderLink ? (
                renderLink(ctaText)
              ) : (
                <TextButton onClick={onCtaClick} textStyle="body">
                  {ctaText}
                </TextButton>
              )}
            </Box>
          )}
          {shouldDisplayAsPreview && hasCtaText && (
            <Paragraph
              css={{
                color: 'blue.400',
                padding: 'default',
                textAlign: 'center',
              }}
            >
              {ctaText}
            </Paragraph>
          )}
        </Box>
        {!!messageDateMs && (
          <MessageFooter isMessageOwner={false} messageDateMs={messageDateMs} metaLabelType="system" />
        )}
      </Box>
    </MediaObject>
  );
}
